import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';
import { CommitmentCloud, CommitmentType } from '@cast/types-pricing';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { overrideOrganizationHeader } from '@cast/api-client';

const apiFn = apiClient.commitments.getCommitmentsUsageHistory;

type ResponseData = ApiResponse<typeof apiFn>;

const COMMITMENTS_USAGE_HISTORY_QUERY_KEY = 'commitments-usage-history';

const getCommitmentsUsageHistoryQueryKey = ({
  organizationId,
  commitmentId,
  startTime,
  endTime,
  cloud,
  commitmentTypes,
}: {
  organizationId: string | undefined;
  commitmentId?: string;
  startTime: string;
  endTime: string;
  cloud?: CommitmentCloud;
  commitmentTypes?: CommitmentType[];
}) =>
  [
    COMMITMENTS_USAGE_HISTORY_QUERY_KEY,
    organizationId,
    commitmentId,
    startTime,
    endTime,
    cloud,
    commitmentTypes,
  ] as const;

const getCommitmentsUsageHistoryQueryOptions = ({
  organizationId,
  commitmentId,
  startTime,
  endTime,
  cloud,
  commitmentTypes,
  aggregationInterval,
  enabled,
}: {
  organizationId: string | undefined;
  commitmentId?: string;
  startTime: string;
  endTime: string;
  cloud?: CommitmentCloud;
  commitmentTypes?: CommitmentType[];
  aggregationInterval: Parameters<typeof apiFn>[0]['aggregationInterval'];
  enabled?: UseCommitmentsUsageHistoryQueryArgs<unknown>['enabled'];
}) =>
  queryOptions({
    queryKey: getCommitmentsUsageHistoryQueryKey({
      organizationId,
      commitmentId,
      startTime,
      endTime,
      cloud,
      commitmentTypes,
    }),
    queryFn: async () => {
      const { data } = await apiFn(
        {
          organizationId: organizationId!,
          ...(commitmentId
            ? { commitmentId }
            : {
                cloud,
                commitmentTypes,
                commitmentId: '-',
              }),
          aggregationInterval,
          startTime,
          endTime,
        },
        {
          // we need this override because sometimes we need to fetch data for enterprise organization (not child organization)
          headers: overrideOrganizationHeader(organizationId),
        }
      );
      return data;
    },
    enabled: makeIsEnabled(
      (!!cloud && !!commitmentTypes?.length) || !!commitmentId,
      enabled
    ),
  });

type UseCommitmentsUsageHistoryQueryArgs<SELECT> = QueryOptions<
  ResponseData,
  SELECT
> & {
  organizationId?: string;
  commitmentId?: string;
  cloud?: CommitmentCloud;
  commitmentTypes?: CommitmentType[];
  startTime: string;
  endTime: string;
  aggregationInterval?: Parameters<typeof apiFn>[0]['aggregationInterval'];
};

export const useCommitmentsUsageHistoryQuery = <SELECT = ResponseData>({
  commitmentId,
  startTime,
  endTime,
  cloud,
  commitmentTypes,
  aggregationInterval = 'HOUR',
  organizationId: organizationIdOverride,
  enabled,
  select,
}: UseCommitmentsUsageHistoryQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = organizationIdOverride || currentOrganization?.id;
  const base = getCommitmentsUsageHistoryQueryOptions({
    organizationId,
    commitmentId,
    startTime,
    endTime,
    cloud,
    commitmentTypes,
    aggregationInterval,
    enabled,
  });

  return useQuery({
    ...base,
    select,
  });
};
